import React, { useMemo } from "react";
import { View, StyleSheet, Platform } from "react-native";

import { useTheme } from "../ThemeProvider";
import type { StrictSpacingStyle } from "../util/types";

/**
 * Props for the Divider component
 */
export interface DividerProps {
  /**
   * When true, uses inverse styling with a different background color for the divider lines.
   * Typically used on dark backgrounds where the default border color may not be visible.
   * @default false
   */
  inverse?: boolean;

  /**
   * When true, renders the divider in vertical orientation (column layout).
   * When false, renders horizontally (row layout).
   * @default false
   */
  vertical?: boolean;

  /**
   * Custom spacing styles to apply to the divider container.
   * Accepts margin and padding properties following the MADS design system spacing tokens.
   */
  sx?: StrictSpacingStyle;

  /**
   * Optional content to display between the divider lines.
   * When provided, the divider will render with content in the center and divider lines on both sides.
   * Common use cases include text labels or icons.
   */
  children?: React.ReactNode;

  /**
   * Custom accessibility label for screen readers.
   * When not provided, defaults to "Divider" for basic dividers without children.
   * When children are present, no default label is applied as the children provide the accessible content.
   */
  accessibilityLabel?: string;

  /**
   * Additional accessibility hint to provide context for screen readers.
   * Provides extra information about the divider's purpose or what action might be available.
   */
  accessibilityHint?: string;
}

export function Divider({
  inverse,
  vertical,
  sx,
  children,
  accessibilityLabel,
  accessibilityHint,
}: DividerProps) {
  const theme = useTheme();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        containerHorizontal: {
          flexDirection: "row",
          alignItems: "center",
          width: "100%",
        },
        containerVertical: {
          flexDirection: "column",
          alignItems: "center",
        },
        dividerHorizontal: {
          height: 1,
          flex: 1,
        },
        dividerVertical: {
          width: 1,
          flex: 1,
        },
        dividerBackground: {
          backgroundColor: theme.colors.borderWeak,
        },
        dividerBackgroundInverse: {
          backgroundColor: theme.colors.borderInverse,
        },
        childrenSpacing: {
          marginHorizontal: theme.spacing.s2,
        },
        childrenSpacingVertical: {
          marginVertical: theme.spacing.s2,
        },
      }),
    [theme],
  );

  const dividerStyle = vertical ? styles.dividerVertical : styles.dividerHorizontal;
  const backgroundStyle = inverse ? styles.dividerBackgroundInverse : styles.dividerBackground;
  const childrenSpacing = vertical ? styles.childrenSpacingVertical : styles.childrenSpacing;
  const containerStyle = vertical ? styles.containerVertical : styles.containerHorizontal;

  // Accessibility props for the container
  const accessibilityProps = {
    // The "accessible" prop translates to focusable on Android and makes it an incorrect keyboard stop.
    accessible: Platform.OS !== "android",
    accessibilityRole: "none" as const, // React Native doesn't have 'separator' role
    accessibilityLabel: accessibilityLabel || (children ? undefined : "Divider"),
    accessibilityHint,
    "aria-hidden": !accessibilityLabel && !children,
  };

  // Render a single container applying sx directly
  return (
    <View style={[containerStyle, sx]} testID="divider-container" {...accessibilityProps}>
      <View style={[dividerStyle, backgroundStyle]} testID="divider-line" />
      {children && (
        <View style={childrenSpacing} testID="divider-children">
          {children}
        </View>
      )}
      <View style={[dividerStyle, backgroundStyle]} testID="divider-line" />
    </View>
  );
}
